import { useEffect, useRef, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import EstadoConsultaBadge from '../components/consultas/EstadoConsultaBadge'
import { Icon } from '../components/ui/Icons'
import { useSocioPortal } from '../contexts/SocioPortalContext'
import { CATEGORIAS_CONSULTA, CONSULTA_MAX, CONSULTA_MIN, categoriaLabel, formatConsultaDate } from '../utils/consultas'

const inputClass = 'mt-2 block min-h-12 w-full rounded-xl border border-border bg-white px-4 py-3 text-base outline-none transition focus:border-secondary focus:ring-4 focus:ring-secondary/15'

function ConsultasView({ listConsultas, createConsulta, signOut }) {
  const [consultas, setConsultas] = useState(null)
  const [categoria, setCategoria] = useState('general')
  const [texto, setTexto] = useState('')
  const [sending, setSending] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [loadError, setLoadError] = useState('')
  const sendingRef = useRef(false)

  useEffect(() => {
    let active = true
    listConsultas()
      .then((data) => { if (active) setConsultas(data) })
      .catch((e) => { if (active && e.status !== 401) setLoadError('No fue posible cargar tus consultas.') })
    return () => { active = false }
  }, [listConsultas])

  const length = texto.trim().length
  const valid = length >= CONSULTA_MIN && length <= CONSULTA_MAX
  const pendientes = (consultas ?? []).filter((item) => item.estado === 'pendiente').length

  async function submit(event) {
    event.preventDefault()
    // El ref evita el doble envío aun antes de que React deshabilite el botón.
    if (sendingRef.current || !valid) return
    sendingRef.current = true
    setSending(true)
    setError('')
    setMessage('')
    try {
      setConsultas(await createConsulta(categoria, texto.trim()))
      setTexto('')
      setCategoria('general')
      setMessage('Consulta enviada correctamente.')
    } catch (e) {
      if (e.status !== 401) setError([400, 409].includes(e.status) && e.message ? e.message : 'No fue posible enviar la consulta.')
    } finally {
      sendingRef.current = false
      setSending(false)
    }
  }

  return (
    <div className="min-h-dvh bg-background">
      <header className="sticky top-0 z-20 border-b border-border bg-white/95 px-4 pt-safe backdrop-blur">
        <div className="mx-auto flex min-h-16 max-w-3xl items-center justify-between gap-3 py-2">
          <Link to="/socio/inicio" className="inline-flex min-h-11 items-center gap-1 rounded-xl px-2 text-sm font-semibold text-primary"><Icon name="chevronLeft" className="h-4 w-4" />Volver</Link>
          <p className="text-xs font-bold uppercase tracking-widest text-primary">Portal del socio</p>
          <button type="button" onClick={signOut} className="inline-flex min-h-11 items-center gap-1.5 rounded-xl border border-border px-3 text-sm font-semibold text-primary" aria-label="Cerrar sesión"><Icon name="logout" className="h-4 w-4" /><span>Salir</span></button>
        </div>
      </header>
      <main className="mx-auto max-w-3xl space-y-6 px-4 pt-6 pb-safe-page">
        <h1 className="text-2xl font-bold sm:text-3xl">Consultas</h1>

        <section className="space-y-3">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Nueva consulta</h2>
          <form onSubmit={submit} noValidate className="space-y-4 rounded-2xl border border-border bg-white p-5 shadow-sm">
            <label className="block text-sm font-semibold">Categoría
              <select value={categoria} onChange={(event) => setCategoria(event.target.value)} className={inputClass}>{CATEGORIAS_CONSULTA.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
            </label>
            <div>
              <label className="block text-sm font-semibold" htmlFor="consulta-texto">Consulta</label>
              <textarea id="consulta-texto" rows={5} maxLength={CONSULTA_MAX} value={texto} onChange={(event) => { setTexto(event.target.value); setMessage('') }} placeholder="Escribe tu consulta brevemente..." className={inputClass + ' resize-y'} aria-describedby="consulta-ayuda" />
              <div id="consulta-ayuda" className="mt-1.5 flex items-start justify-between gap-3 text-sm text-text-secondary"><span>Máximo 300 caracteres.</span><span className={'shrink-0 font-semibold ' + (texto.length >= CONSULTA_MAX ? 'text-danger' : '')}>{texto.length} / {CONSULTA_MAX}</span></div>
              {texto.length > 0 && length < CONSULTA_MIN && <p className="mt-1 text-sm text-danger">Escribe al menos {CONSULTA_MIN} caracteres.</p>}
            </div>
            {pendientes >= 3 && <p className="text-sm text-danger">Tienes 3 consultas pendientes. Espera una respuesta antes de enviar otra.</p>}
            {message && <div className="rounded-xl border border-success/30 bg-success/10 p-3 text-sm font-semibold text-success-text" role="status">{message}</div>}
            {error && <div className="rounded-xl border border-danger/25 bg-danger/5 p-3 text-sm text-danger" role="alert">{error}</div>}
            <button type="submit" disabled={sending || !valid || pendientes >= 3} className="min-h-12 w-full rounded-xl bg-primary px-5 py-3 text-base font-semibold text-white shadow-lg shadow-primary/20 transition hover:bg-primary-hover disabled:opacity-60 sm:w-auto">{sending ? 'Enviando...' : 'Enviar consulta'}</button>
          </form>
        </section>

        <section className="space-y-3">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Mis consultas</h2>
          {loadError ? <p className="rounded-2xl border border-danger/25 bg-danger/5 p-5 text-sm text-danger" role="alert">{loadError}</p>
            : consultas === null ? <p className="rounded-2xl border border-border bg-white p-5 text-sm text-text-secondary" role="status">Cargando…</p>
            : consultas.length === 0 ? <p className="rounded-2xl border border-border bg-white p-5 text-sm text-text-secondary">Aún no has enviado consultas.</p>
            : <div className="space-y-3">{consultas.map((item) => (
              <article key={item.id} className="rounded-2xl border border-border bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2"><p className="font-bold text-primary">{categoriaLabel(item.categoria)}</p><EstadoConsultaBadge estado={item.estado} /></div>
                <p className="mt-3 whitespace-pre-wrap break-words">{item.consulta}</p>
                {item.respuesta && <div className="mt-4 rounded-xl bg-secondary/10 p-4"><p className="text-xs font-bold uppercase tracking-wide text-primary">Respuesta de la Directiva</p><p className="mt-1 whitespace-pre-wrap break-words">{item.respuesta}</p></div>}
                <p className="mt-3 text-sm text-text-secondary">{formatConsultaDate(item.created_at)}</p>
              </article>
            ))}</div>}
        </section>
      </main>
    </div>
  )
}

export default function SocioConsultasPage() {
  const { profile, loading, token, signOut, listConsultas, createConsulta } = useSocioPortal()
  if (loading) return <div className="grid min-h-dvh place-items-center bg-background text-text-secondary" role="status">Cargando…</div>
  if (!token || !profile) return <Navigate to="/socio/login" replace />
  return <ConsultasView listConsultas={listConsultas} createConsulta={createConsulta} signOut={signOut} />
}
